import {
  Box,
  Card,
} from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import { observer } from "mobx-react";
import { OrderRequest } from "../../../models/Order/OrderRequest";

/** Same rounding as backend payment link (`total_amount * exchange_rate`). */
function roundMoney2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

interface OrderCalculationsTableProps {
  orderDetails?: OrderRequest;
  padding?: string;
  boxShadow?: string;
}

const OrderCalculationsTable: React.FC<OrderCalculationsTableProps> = ({
  orderDetails,
  padding,
  boxShadow,
}) => {
  const balance =
    orderDetails?.total_amount != null && orderDetails?.amount_received != null
      ? orderDetails.amount_received > orderDetails.total_amount
        ? "0"
        : Number.isInteger(
          orderDetails.total_amount - orderDetails.amount_received,
        )
          ? String(orderDetails.total_amount - orderDetails.amount_received)
          : (
            orderDetails.total_amount - orderDetails.amount_received
          ).toFixed(2)
      : "--";
  const balanceInSelectedCurrency =
    orderDetails?.total_amount != null &&
      orderDetails?.amount_received != null &&
      orderDetails?.exchange_rate != null &&
      orderDetails.exchange_rate > 0
      ? (() => {
        const base =
          orderDetails.amount_received > orderDetails.total_amount
            ? 0
            : orderDetails.total_amount - orderDetails.amount_received;
        const fx = roundMoney2(base * orderDetails.exchange_rate);
        const s = Number.isInteger(fx) ? String(fx) : fx.toFixed(2);
        return orderDetails.currency ? `${s} ${orderDetails.currency}` : s;
      })()
      : "--";

  const metrics: { label: string; value: React.ReactNode }[] = [
    { label: "Subtotal", value: orderDetails?.amount_before_discount ?? "--" },
    { label: "Discount (Products)", value: orderDetails?.discount || "--" },
    {
      label: "Discount% (Products)",
      value: orderDetails?.discount_percentage || "--",
    },
    { label: "Pre GST", value: orderDetails?.net_amount ?? "--" },
    { label: "Taxes", value: orderDetails?.tax_amount || "--" },
    { label: "Total", value: orderDetails?.total_amount || "--" },
    { label: "Amount Received", value: orderDetails?.amount_received || "--" },
    {
      label: "Exchange Rate",
      value: orderDetails?.exchange_rate
        ? `${orderDetails.exchange_rate}-${orderDetails.currency}`
        : "--",
    },
    { label: "Balance", value: balance },
    {
      label: "Balance (selected currency)",
      value: balanceInSelectedCurrency,
    },
  ];

  return (
    <Box sx={{ borderRadius: 2 }}>
      <Card
        sx={{
          p: padding || 5,
          boxShadow:
            boxShadow ||
            "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
        }}
      >
        <CustomTypography variant="heading4" fontWeight={700} sx={{ mb: 3 }}>
          Calculations
        </CustomTypography>
        {!orderDetails ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CustomTypography
              variant="body2"
              fontWeight={700}
              color="rgba(218, 218, 218, 1)"
            >
              NOTHING FOUND!
            </CustomTypography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 3,
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              backgroundColor: "#FAFAFA",
              border: "1px solid #E2E8F0",
              borderRadius: "10px",
              p: "16px 20px",
            }}
          >
            {metrics?.map((m) => (
              <Box
                key={m.label}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                  minWidth: 100,
                  flexShrink: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.63rem",
                    color: "#94A3B8",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {m.label}
                </Box>
                <Box
                  component="span"
                  sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#334155" }}
                >
                  {m.value}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default observer(OrderCalculationsTable);
